/**
 * Turns a worker article (a full HTML document or fragment) into three separate things:
 *   1. the readable article HTML, sanitised and safe to drop into a script-less iframe,
 *   2. the JSON-LD blocks (shown in a collapsed "Schema" panel, never inline),
 *   3. the `<!-- SOURCES ... -->` ledger (shown in a collapsed "Sources" panel).
 *
 * The raw tags are never shown to the owner.
 */
import DOMPurify from "dompurify";
import { decodeEntities } from "./helpers";

export interface SplitArticle {
  /** Sanitised article HTML (no scripts, comments, event handlers or javascript: links). */
  html: string;
  /** Pretty-printed JSON-LD blocks, in document order. */
  jsonLd: { type: string; text: string }[];
  /** Raw sources ledger text (markdown-ish), without the comment delimiters. */
  sources: string | null;
  /** Number of sourced facts in the ledger (F1, F2 ...), or null when it cannot be told. */
  sourcesCount: number | null;
}

const LD_JSON_RE = /<script\b[^>]*type\s*=\s*["']?application\/ld\+json["']?[^>]*>([\s\S]*?)<\/script\s*>/gi;
const SOURCES_RE = /<!--\s*SOURCES\b([\s\S]*?)-->/gi;

function prettyJsonLd(raw: string): { type: string; text: string } {
  const trimmed = raw.trim();
  try {
    const parsed = JSON.parse(trimmed);
    const t = Array.isArray(parsed) ? parsed.map((p) => p?.["@type"]).filter(Boolean).join(", ") : parsed?.["@type"];
    return { type: typeof t === "string" && t ? t : "Structured data", text: JSON.stringify(parsed, null, 2) };
  } catch {
    return { type: "Structured data", text: trimmed };
  }
}

export function countSources(ledger: string): number | null {
  const facts = ledger.match(/^\s{0,3}#{2,4}\s*F\d+\b/gim);
  if (facts && facts.length) return facts.length;
  const urls = ledger.match(/^\s*[-*]\s*url:/gim);
  return urls && urls.length ? urls.length : null;
}

const FORBID_TAGS = [
  "script", "style", "form", "input", "button", "textarea", "select", "option", "iframe", "object",
  "embed", "link", "meta", "base", "noscript", "template", "svg", "math", "video", "audio", "source", "track",
];

function sanitizeToHtml(html: string, dropTitle: string | null): string {
  const body = DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    FORBID_TAGS,
    FORBID_ATTR: ["srcset", "formaction", "action", "ping", "autofocus"],
    ALLOW_DATA_ATTR: false,
    RETURN_DOM: true,
  }) as unknown as HTMLElement;

  // Links: only real web / mail links stay clickable, and they open in a new tab.
  body.querySelectorAll("a").forEach((a) => {
    const href = (a.getAttribute("href") || "").trim();
    if (/^(https?:|mailto:|tel:)/i.test(href)) {
      a.setAttribute("target", "_blank");
      a.setAttribute("rel", "noopener noreferrer nofollow");
    } else {
      a.removeAttribute("href");
      a.removeAttribute("target");
    }
  });
  // Inline styles may stay (callout boxes) but nothing that can fetch or execute.
  body.querySelectorAll("[style]").forEach((el) => {
    const s = el.getAttribute("style") || "";
    if (/url\s*\(|expression\s*\(|@import|behavior\s*:|-moz-binding|javascript:/i.test(s)) el.removeAttribute("style");
  });
  body.querySelectorAll("img").forEach((img) => {
    const src = (img.getAttribute("src") || "").trim();
    if (!/^(https?:\/\/|data:image\/(png|jpe?g|gif|webp);)/i.test(src)) {
      img.remove();
      return;
    }
    img.setAttribute("loading", "lazy");
    img.setAttribute("referrerpolicy", "no-referrer");
  });

  // The decision header already shows the title; do not repeat it as the first heading.
  if (dropTitle) {
    const h1 = body.querySelector("h1");
    const norm = (s: string) => decodeEntities(s).replace(/\s+/g, " ").trim().toLowerCase();
    if (h1 && norm(h1.textContent || "") === norm(dropTitle)) h1.remove();
  }
  return body.innerHTML;
}

export function splitArticleHtml(raw: string, title?: string | null): SplitArticle {
  let work = raw || "";

  const jsonLd: { type: string; text: string }[] = [];
  work = work.replace(LD_JSON_RE, (_m, inner: string) => {
    if (inner && inner.trim()) jsonLd.push(prettyJsonLd(inner));
    return "";
  });

  const ledgers: string[] = [];
  work = work.replace(SOURCES_RE, (_m, inner: string) => {
    if (inner && inner.trim()) ledgers.push(inner.trim());
    return "";
  });
  const sources = ledgers.length ? ledgers.join("\n\n") : null;

  // Everything left in <head> (title/meta/style) is not article content.
  work = work.replace(/<head\b[\s\S]*?<\/head\s*>/gi, "");
  // Remaining comments (marker comments such as insight-disclaimer-v2) are never shown.
  work = work.replace(/<!--[\s\S]*?-->/g, "");

  return {
    html: sanitizeToHtml(work, title ?? null),
    jsonLd,
    sources,
    sourcesCount: sources ? countSources(sources) : null,
  };
}

/** The document placed in the iframe: a light "paper" page so the article reads like the live site. */
export function buildSrcDoc(articleHtml: string): string {
  return `<!doctype html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><base target="_blank"><style>
html,body{margin:0;padding:0;background:#fff;color:#1a1a1a}
body{display:flow-root;padding:28px 32px 36px;font:16px/1.7 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;overflow-wrap:anywhere;overflow-x:hidden}
h1{font-size:1.9em;line-height:1.25;margin:0 0 .6em}
h2{font-size:1.45em;line-height:1.3;margin:1.8em 0 .5em}
h3{font-size:1.15em;line-height:1.35;margin:1.4em 0 .4em}
p{margin:0 0 1em}
ul,ol{padding-left:1.4em;margin:0 0 1em}
li{margin:.2em 0}
a{color:#2563eb}
img{max-width:100%;height:auto}
table{border-collapse:collapse;max-width:100%;display:block;overflow-x:auto}
td,th{border:1px solid #d6d6e0;padding:6px 10px;text-align:left;vertical-align:top}
blockquote{margin:1em 0;padding-left:1em;border-left:3px solid #c7c7d6;color:#444}
pre,code{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:.9em;white-space:pre-wrap}
@media (max-width:520px){body{padding:18px 16px 24px;font-size:15px}}
</style></head><body>${articleHtml}</body></html>`;
}
